import React, { useState } from 'react';
import PropTypes from 'prop-types';

import AppointmentsService from '../../../../services/AppointmentsService';
import CustomModal from '../../../blocks/custom-modal';
import InfoBox from '../../../blocks/messages/info-box';

/**
 * DeleteAppointmentActionModal Component
 *
 * This component displays a modal for confirming the deletion of an appointment action.
 * It takes `open`, `onClose`, `actionId`, and `onDelete` as props.
 *
 * Props:
 * - open (boolean): Controls the modal visibility.
 * - onClose (function): Function to close the modal.
 * - actionId (number): The ID of the action to delete.
 * - onDelete (function): Function to handle the deletion after success.
 *
 * @param {object} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const DeleteAppointmentActionModal = ({ open, onClose, actionId, onDelete }) => {
    // State to handle error messages
    const [error, setError] = useState(null);

    /**
     * Handles the deletion confirmation
     *
     * @returns {void}
     */
    const handleConfirmDelete = async () => {
        if (actionId) {
            try {
                // Delete the action
                await AppointmentsService.deleteAppointmentAction(actionId);

                // Trigger the onDelete callback
                onDelete();

                // Close the modal after the operation
                onClose();
            } catch (error) {
                setError(error?.message || "Une erreur inattendue est survenue");
            }
        }
    };

    // Return null if the modal is not open or if there is no action ID
    if (!open || !actionId) return null;

    // Render the modal
    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={'Confirmer la suppression'}
            handleSave={handleConfirmDelete}
            handleSaveLabel="Confirmer la suppression"
            onCloseLabel={'Annuler'}
        >
            {error ? (
                <InfoBox
                    type="error"
                    text={error}
                    disabledCloseBtn={true}
                    
                />
            ) : (
                <InfoBox
                    type="warning"
                    text={
                        <p>Êtes-vous sûr de vouloir supprimer cette action de rendez-vous ? Cette action est irréversible.</p>
                    }
                    disabledCloseBtn={true}
                />
            )}
        </CustomModal>
    );
};

// Prop types for the DeleteAppointmentActionModal component
DeleteAppointmentActionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    actionId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    onDelete: PropTypes.func.isRequired,
};

export default DeleteAppointmentActionModal;
